import { BACKEND_URL } from "../../lib/backend";
/**
 * YouTubeWatchlistWidget — Admin UI for managing the 12h cron's channel list.
 *
 * Backed by /api/admin/youtube/watchlist (GET/POST/PATCH/DELETE) and
 * /api/admin/youtube/watchlist/run-now (manual trigger).
 *
 * Self-contained: handles its own data fetching, no props beyond `dm` for
 * dark-mode coordination with the parent SourcesTab.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Youtube, Plus, RefreshCw, Trash2, PlayCircle, Clock, AlertCircle } from "lucide-react";

const API = `${BACKEND_URL}/api`;

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return iso; }
}

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

export default function YouTubeWatchlistWidget({ dm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [newMax, setNewMax] = useState(3);
  const [adding, setAdding] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/youtube/watchlist`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load YouTube watchlist"));
    } finally {
      setLoading(false);
    }
  }, []);

   
  useEffect(() => { load(); }, [load]);

  const addChannel = async () => {
    const url = (newUrl || "").trim();
    if (!url) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/admin/youtube/watchlist`, {
        channel_url: url,
        name: newName.trim() || null,
        max_per_run: parseInt(newMax, 10) || 3,
        enabled: true,
      });
      if (r.data.existing) {
        toast.info("Channel already in watchlist");
      } else {
        toast.success(`Channel added: ${r.data.item.name}`);
      }
      setNewUrl(""); setNewName(""); setNewMax(3);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to add channel"));
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (item) => {
    try {
      await axios.patch(`${API}/admin/youtube/watchlist/${item.id}`, { enabled: !item.enabled });
      toast.success(`${item.name} ${!item.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Toggle failed"));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from the watchlist?`)) return;
    try {
      await axios.delete(`${API}/admin/youtube/watchlist/${item.id}`);
      toast.success("Channel removed");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  const runNow = async () => {
    if (!items.length) {
      toast.error("Add a channel first");
      return;
    }
    setRunningNow(true);
    toast.info("Running YouTube watchlist now (may take 1-2 min)…");
    try {
      const r = await axios.post(`${API}/admin/youtube/watchlist/run-now`, {}, { timeout: 300000 });
      const d = r.data;
      toast.success(
        `Done — ${d.channels_processed} channels, ${d.inserted} new videos, ` +
        `${d.skipped_existing} duplicates${d.errors?.length ? `, ${d.errors.length} errors` : ""}`
      );
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Run failed"));
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div
      className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}
      data-testid="youtube-watchlist-widget"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-red-400/10 text-red-400" : "bg-red-500/10 text-red-600"}`}>
          <Youtube className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>YouTube watchlist — auto-ingestion every 12h</p>
          <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Channels listed here are scanned twice a day. New videos are pulled with their transcripts via yt-dlp and added to the feed as <code>discoveryMethod=youtube</code>. Each channel ingests at most <em>max_per_run</em> videos per scan.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={runningNow || !items.length}
          data-testid="yt-watchlist-run-now-btn"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
        >
          {runningNow ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
          Run now
        </button>
      </div>

      {/* Add channel row */}
      <div className={`rounded-md p-3 mb-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`} data-testid="yt-watchlist-add-row">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
          <Input
            placeholder="https://www.youtube.com/@channel"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            data-testid="yt-watchlist-url-input"
            className="md:col-span-6 text-sm border-0 bg-transparent"
          />
          <Input
            placeholder="Display name (optional)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="yt-watchlist-name-input"
            className="md:col-span-3 text-sm border-0 bg-transparent"
          />
          <Input
            type="number"
            min={1}
            max={10}
            value={newMax}
            onChange={(e) => setNewMax(e.target.value)}
            placeholder="max/run"
            data-testid="yt-watchlist-max-input"
            className="md:col-span-1 text-sm border-0 bg-transparent text-center"
          />
          <button
            onClick={addChannel}
            disabled={!newUrl.trim() || adding}
            data-testid="yt-watchlist-add-btn"
            className={`md:col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
          >
            {adding ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className={`text-center py-6 text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Loading…</div>
      ) : items.length === 0 ? (
        <div className={`text-center py-6 text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="yt-watchlist-empty">
          No channels yet. Add a YouTube channel above to start auto-ingesting videos every 12h.
        </div>
      ) : (
        <div className="space-y-2" data-testid="yt-watchlist-items">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`yt-watchlist-item-${it.id}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-md ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}
            >
              <Switch
                checked={it.enabled}
                onCheckedChange={() => toggle(it)}
                data-testid={`yt-watchlist-toggle-${it.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{it.name}</span>
                  <span className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>max {it.max_per_run}/run</span>
                </div>
                <a
                  href={it.channel_url}
                  target="_blank"
                  rel="noreferrer"
                  className={`text-xs truncate block ${dm ? "text-slate-500 hover:text-cyan-400" : "text-[#6b7a7d] hover:text-[#006874]"}`}
                >
                  {it.channel_url}
                </a>
              </div>
              <div className="flex flex-col items-end text-right flex-shrink-0">
                <span className={`category-tag text-xs flex items-center gap-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  <Clock className="w-3 h-3" />
                  {fmtRelative(it.last_ran_at)}
                </span>
                <span className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  last: {it.last_ingested_count ?? 0} new
                </span>
                {it.last_error && (
                  <span className={`category-tag text-xs flex items-center gap-1 ${dm ? "text-rose-400" : "text-rose-600"}`} title={it.last_error}>
                    <AlertCircle className="w-3 h-3" />
                    error
                  </span>
                )}
              </div>
              <button
                onClick={() => remove(it)}
                data-testid={`yt-watchlist-delete-${it.id}`}
                className={`p-1.5 rounded-md transition-colors ${dm ? "hover:bg-rose-400/10 text-slate-500 hover:text-rose-300" : "hover:bg-rose-50 text-[#6b7a7d] hover:text-rose-600"}`}
                title="Remove from watchlist"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
